import {
  AccountViewListingGroupType,
  type AccountViewListingsDto,
  AccountViewListingStatus,
  PropertyType,
  type RentalManagerUserPropertyListDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import QueryConstants from '../../srcLegacy/shared/constants/QueryConstants';
import PropertiesConstants from '../constants/PropertiesConstants';
import { ROUTES } from '../constants/urls';

import type { NextRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import type { AccountViewListingGroupDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { BannerToCloseArg } from '../containers/PropertiesPage';

export type FilterState = {
  subNavFilterType: string;
  searchText: string;
};

export const isFromMarketingPaymentsRoute = (query: ParsedUrlQuery): boolean =>
  query[QueryConstants.MARKETING_PATH] === 'payments';

export const isFromMarketingApplicationsRoute = (query: ParsedUrlQuery): boolean =>
  query[QueryConstants.MARKETING_PATH] === 'applications';

export const isFromMarketingLeasesRoute = (query: ParsedUrlQuery): boolean =>
  query[QueryConstants.MARKETING_PATH] === 'leases';

export const isRedirectToPayments = (query: ParsedUrlQuery): boolean =>
  query[QueryConstants.REDIRECT_TO] === 'payments';

export const initializeFiltersFromRouter = (query: NextRouter['query'], defaults: FilterState): FilterState => ({
  ...defaults,
  subNavFilterType: (query[QueryConstants.SUB_NAV_FILTER_TYPE] as string) || defaults.subNavFilterType,
  searchText: (query[QueryConstants.SEARCH_TEXT] as string) || defaults.searchText,
});

export const updateRouterWithFilters = (router: NextRouter, filters: FilterState) => {
  // Preserve all existing query params
  const query = { ...router.query };
  let hasChanges = false;

  // Update subNavFilterType
  const currentSubNavFilterType = query[QueryConstants.SUB_NAV_FILTER_TYPE] as string | undefined;
  const newSubNavFilterType = filters.subNavFilterType;

  if (currentSubNavFilterType !== newSubNavFilterType) {
    query[QueryConstants.SUB_NAV_FILTER_TYPE] = newSubNavFilterType;
    hasChanges = true;
  }

  // Update searchText
  const currentSearchText = query[QueryConstants.SEARCH_TEXT] as string | undefined;
  const newSearchText = filters.searchText;

  if (currentSearchText !== newSearchText) {
    if (newSearchText && newSearchText.trim() !== '') {
      query[QueryConstants.SEARCH_TEXT] = newSearchText;
    } else {
      delete query[QueryConstants.SEARCH_TEXT];
    }
    hasChanges = true;
  }

  // Only update URL if there are actual changes
  if (hasChanges) {
    router.replace({ pathname: ROUTES.PROPERTIES, query }, undefined, { shallow: true });
  }
};

export const removeQueryParams = (router: NextRouter, paramsToRemove: Array<string>) => {
  const query = { ...router.query };
  let hasChanges = false;

  paramsToRemove.forEach((param) => {
    if (query[param]) {
      delete query[param];
      hasChanges = true;
    }
  });

  // Only update URL if there are actual changes
  if (hasChanges) {
    router.replace({ pathname: ROUTES.PROPERTIES, query }, undefined, { shallow: true });
  }
};

export const renderInitialDisplayModal = ({
  router,
  totalListingsWithoutFilters,
  setShowAddPropertyModal,
  canAccessUserOnboardingNewUser,
  properties,
  showAddPropertyModal,
  setShowAddFeedListingsModal,
  setShowPostClaimingModal,
  setShowClaimPropertiesModal,
  canAccessClaimPathModal,
  canAccessClaimPathMorePropertiesModal,
  setShowSpotlightListingsFirstUseModal,
  canAccessSpotlightListingsFirstUseModal,
  setShowingUserOnboardingExisitngUser,
  canAccessUserOnboardingExistingUser,
}: {
  router: NextRouter;
  totalListingsWithoutFilters: number;
  showAddPropertyModal: boolean;
  setShowAddPropertyModal: ({ shouldSendAnalytics }: { shouldSendAnalytics?: boolean }) => void;
  canAccessUserOnboardingNewUser: boolean;
  properties: AccountViewListingsDto | RentalManagerUserPropertyListDto;
  setShowAddFeedListingsModal: () => void;
  setShowPostClaimingModal?: (isOpen: boolean) => void;
  setShowClaimPropertiesModal?: ({ isOpen, isClaimMoreModal }: { isOpen: boolean; isClaimMoreModal: boolean }) => void;
  canAccessClaimPathModal?: boolean;
  canAccessClaimPathMorePropertiesModal?: boolean;
  setShowSpotlightListingsFirstUseModal?: (isOpen: boolean) => void;
  canAccessSpotlightListingsFirstUseModal?: boolean;
  setShowingUserOnboardingExisitngUser?: (isOpen: boolean) => void;
  canAccessUserOnboardingExistingUser?: boolean;
}) => {
  const query = router.query;

  const isFromMarketingPayments = isFromMarketingPaymentsRoute(query);
  const isFromMarketingApplications = isFromMarketingApplicationsRoute(query);
  const isFromMarketingLeases = isFromMarketingLeasesRoute(query);
  const isFromLandingPage = query[QueryConstants.POSTING_PATH] === 'true';
  const isFromPaymentsAggregateViewPage = isRedirectToPayments(query);
  const isFromClaimDashboard = query[QueryConstants.CLAIM_DASHBOARD] === 'true';
  const foundAddAFeedParam = query[QueryConstants.ADD_A_FEED] === 'true';

  if (isFromMarketingPayments || isFromMarketingApplications || isFromMarketingLeases) {
    // Determine marketing route from query params
    const marketingRoute = isFromMarketingPayments
      ? 'payments'
      : isFromMarketingApplications
        ? 'applications'
        : 'leases';

    // if no listings and not already onboarding, show add property modal
    if (totalListingsWithoutFilters === 0 && !showAddPropertyModal && !canAccessUserOnboardingNewUser) {
      setShowAddPropertyModal({ shouldSendAnalytics: true });
      return;
    }

    // if 1 listing go to PDP (payments/applications/leases tab)
    if (totalListingsWithoutFilters === 1) {
      const { listingAlias } = properties.listings[0];
      const searchString = new URLSearchParams(query as Record<string, string>).toString();
      const targetRoute = `/properties/${listingAlias}/${marketingRoute}${searchString ? `?${searchString}` : ''}`;

      // nav to pdp (using window.location for cross-route navigation)
      window.location.href = targetRoute;
      return;
    }
  }

  if (isFromPaymentsAggregateViewPage && !showAddPropertyModal && !canAccessUserOnboardingNewUser) {
    // track only if routed from Aggregate View page
    setShowAddPropertyModal({ shouldSendAnalytics: true });
    return;
  }

  if (canAccessUserOnboardingExistingUser) {
    setShowingUserOnboardingExisitngUser?.(true);
    return;
  }

  const isFromSignInLPAndNoListings = query[QueryConstants.SIGN_IN_LP] === 'true' && totalListingsWithoutFilters === 0;
  // if coming from zillow landing page or hotpads post a listing link and `AddPropertyModal` and UserOnboardingNewUser is not already displaying
  if ((isFromLandingPage || isFromSignInLPAndNoListings) && !showAddPropertyModal && !canAccessUserOnboardingNewUser) {
    // track only if coming from Get Started button on landing page or if routed from Aggregate View page
    setShowAddPropertyModal({ shouldSendAnalytics: isFromLandingPage });
    return;
  }

  if (foundAddAFeedParam) {
    setShowAddFeedListingsModal();
    return;
  }

  if (isFromClaimDashboard) {
    setShowPostClaimingModal?.(true);
    return;
  }

  if (canAccessClaimPathModal || canAccessClaimPathMorePropertiesModal) {
    setShowClaimPropertiesModal?.({
      isOpen: true,
      isClaimMoreModal: canAccessClaimPathMorePropertiesModal ?? false,
    });
    return;
  }

  if (canAccessSpotlightListingsFirstUseModal) {
    setShowSpotlightListingsFirstUseModal?.(true);
    return;
  }
};

export const closeBanner = (
  bannerToClose: BannerToCloseArg,
  setDisplayPaymentsNotification: (isOpen: boolean) => void,
  setDisplayApplicationsNotification: (isOpen: boolean) => void,
  setDisplayLeasesNotification: (isOpen: boolean) => void,
  setDisplayListingClaimCompleteBanner: (isOpen: boolean) => void,
) => {
  switch (bannerToClose) {
    case 'displayPaymentsNotification':
      setDisplayPaymentsNotification(false);
      break;
    case 'displayApplicationsNotification':
      setDisplayApplicationsNotification(false);
      break;
    case 'displayLeasesNotification':
      setDisplayLeasesNotification(false);
      break;
    case 'displayListingClaimCompleteBanner':
      setDisplayListingClaimCompleteBanner(false);
      break;
    default:
      break;
  }
};

export const getPhotoUrls = (isRunningInProd: boolean, photoKey: string) => {
  const environmentHost = isRunningInProd ? 'photos.zillowstatic.com' : 'photos.test.zillow.net';

  return {
    medium: `https://${environmentHost}/fp/${photoKey}-rentals_medium_500_500.webp`,
    thumb: `https://${environmentHost}/fp/${photoKey}-rentals_thumb_180_180.webp`,
  };
};

export const transformFiltersToApiParams = (filters: FilterState) => ({
  includeActive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeInactive:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ||
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  includeArchived: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeUnarchived: filters.subNavFilterType !== PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
  includeFreeClaimedListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED,
  includeFreeBonusListingsOnly:
    filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  includeAtRiskOnly: filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  searchText: filters.searchText,
  limit: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT,
  startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
});

export const multiplexSkeletonPlaceholderListing = (parentGroupId?: string): AccountViewListingGroupDto => ({
  type: AccountViewListingGroupType.LISTING,
  groupId: parentGroupId || '',
  listingAlias: '',
  name: '',
  photoKey: '',
  propertyType: PropertyType.UNKNOWN,
  status: {
    status: AccountViewListingStatus.OFF_MARKET,
    tagAppearance: '',
    text: '',
  },
  isMultifamily: false,
  address: { city: '', state: '', street: '', unit: '', zip: '' },
  listingCount: {
    active: 0,
    archived: 0,
    atRisk: 0,
    freeBonusListingCount: 0,
    freeClaimedListingCount: 0,
    freeListingsOwnedByCustomer: 0,
    inactive: 0,
    total: 0,
  },
});
